import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  CaretLeft,
  CaretRight,
  CalendarBlank,
  X,
  Gavel,
  ChatCircle,
  Briefcase,
  ListChecks,
  ArrowSquareOut,
  Clock,
  MapPin,
  PencilSimple,
  Trash,
  ArrowsClockwise,
  MicrosoftOutlookLogo,
  GoogleLogo,
  AppleLogo,
  User,
  Warning,
} from '@phosphor-icons/react';
import { usePreferences } from '@/lib/preferences';
import { useFormat } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/api';
import {
  useDiaryStore,
  dateKey,
  nextEventId,
  HOURS,
  KIND_LABEL,
  CREATABLE_KINDS,
  KIND_TONE,
  TONE_STYLE,
  AVAIL_STYLE,
  AVAIL_LABEL,
  eventStartLabel,
  eventEndLabel,
  timeToSlotIndex,
  EVENT_STATUSES,
  EVENT_STATUS_LABEL,
  EVENT_STATUS_BADGE,
  type EventStatus,
  type CalEvent,
  type EventKind,
  type EventTone,
  type AvailStatus,
  todayKey as getTodayKey,
} from '@/lib/diaryStore';
import { nowLocalTime, todayLocalDate } from '@/lib/datetime';
import { useAuthStore } from '@/features/auth/store';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { useHearingsStore } from '@/lib/hearingsStore';
import { getStatusConfig } from '@/lib/hearingConstants';
import { Trophy, CheckCircle as CheckCircleIcon } from '@phosphor-icons/react';
import { fetchTasks, type Task, STATUS_LABELS, PRIORITY_LABELS } from '@/lib/tasksApi';
import { useMattersStore } from '@/lib/mattersStore';
import { fetchClerksForBarrister, type ClerkAssignment } from '@/lib/barristerClerksApi';
import { isOpenMatterStatus } from '@/lib/matterStatus';
import { useBarristerBusy } from '@/lib/useBarristerBusy';
import { BarristerDayTimeline } from '@/components/shared/BarristerDayTimeline';
import { DoubleBookConfirm, type ClashItem } from '@/components/shared/DoubleBookConfirm';
import {
  fetchCalendarSyncStatus, enableCalendarSync, disableCalendarSync, resyncCalendar,
  type CalendarSyncProviderId,
} from '@/lib/calendarSyncApi';
import { beginOAuthConnect } from '@/lib/mailboxApi';
import { fetchDiaryItemDetail, type DiaryItemDetail } from '@/lib/meApi';
import { canWriteDiary } from '@/lib/pupilUiBoundary';
import { ScrollLock } from '@/components/shared/ScrollLock';

/* ─── Icon map ─── */

const KIND_ICON: Record<EventKind, React.ElementType> = {
  hearing: Gavel, conference: ChatCircle, task: ListChecks, brief_return: Briefcase, personal: CalendarBlank,
  meeting: User, travel: MapPin, leave: CalendarBlank,
};

const TONE_DOT: Record<EventTone, string> = {
  brand: 'bg-brand-500', amber: 'bg-amber-500', red: 'bg-red-500', emerald: 'bg-emerald-500', violet: 'bg-violet-500',
};

const AVAIL_TINT: Record<AvailStatus, string> = {
  available: 'bg-emerald-50/60 dark:bg-emerald-500/10', partial: 'bg-amber-50/60 dark:bg-amber-500/10', busy: 'bg-brand-50/60', leave: 'bg-slate-100/80 dark:bg-slate-800',
};

const DAYS_HEADER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_PX = 56;

/**
 * The week and day grids cover 09:00–17:00 and nothing else (`HOURS`, diaryStore.ts:107), but
 * the slot index is unbounded: `calendarApi.ts:82` computes `Math.max(0, UTCHours - 9)`, so a
 * 19:00 hearing lands at slot 10 and an absolutely positioned block at `10 × 56 + 2 = 562px`
 * — 58px BELOW a 504px grid. With nothing clipping the column, that block escaped the
 * calendar entirely and was seen floating over the legend at the foot of the page.
 *
 * Clamping keeps the block inside the grid and clickable, so the event is still reachable and
 * its real time is on the detail panel. It is NOT hidden — an out-of-hours hearing a barrister
 * cannot see is worse than one drawn at the edge of the working day.
 */
const GRID_PX = HOURS.length * HOUR_PX;
const MIN_BLOCK_PX = 20;

function blockBox(start: number, span: number): { top: string; height: string } {
  const top = Math.min(Math.max(start * HOUR_PX + 2, 2), GRID_PX - MIN_BLOCK_PX);
  const height = Math.max(MIN_BLOCK_PX, Math.min(span * HOUR_PX - 4, GRID_PX - top - 2));
  return { top: `${top}px`, height: `${height}px` };
}

type ViewMode = 'month' | 'week' | 'day';

/** TONE_STYLE from the store omits hover; add it here for the calendar blocks. */
const TONE_HOVER: Record<EventTone, string> = {
  brand:   TONE_STYLE.brand   + ' hover:bg-brand-100',
  amber:   TONE_STYLE.amber   + ' hover:bg-amber-100 dark:hover:bg-amber-500/20 dark:bg-amber-500/15',
  red:     TONE_STYLE.red     + ' hover:bg-red-100 dark:hover:bg-red-500/20 dark:bg-red-500/15',
  emerald: TONE_STYLE.emerald + ' hover:bg-emerald-100 dark:hover:bg-emerald-500/20 dark:bg-emerald-500/15',
  violet:  TONE_STYLE.violet  + ' hover:bg-violet-100 dark:hover:bg-violet-500/20 dark:bg-violet-500/15',
};

/* ═══════════════════════════════════════════════════════════════ */

export default function MyDiaryPage() {
  const prefs = usePreferences();
  // Dates and times are written the way THIS person asked for. The diary's own times are
  // practice wall-clock and stay that way — only the notation changes (see lib/format.ts).
  const { formatDate, formatWallTime, formatMonthYear } = useFormat();
  // Which view this diary opens on is the barrister's own choice (`diary.viewPreferences`). It
  // was stored and read by nothing, so the diary always opened on Month however the setting
  // read. `null` means they have never chosen, which keeps the previous default.
  const [view, setView] = useState<ViewMode>(() => {
    const stored = prefs.diaryDefaultView;
    return stored === 'day' || stored === 'week' || stored === 'month' ? stored : 'month';
  });
  const [adoptedView, setAdoptedView] = useState(false);
  // Preferences arrive after the first paint; adopt once, and never over a view the person has
  // since clicked.
  useEffect(() => {
    if (adoptedView || !prefs.initialised) return;
    const stored = prefs.diaryDefaultView;
    if (stored === 'day' || stored === 'week' || stored === 'month') setView(stored);
    setAdoptedView(true);
  }, [adoptedView, prefs.initialised, prefs.diaryDefaultView]);

  // Working days are the person's own (`diary.workingHours`). The grid hardcoded Saturday and
  // Sunday, so a barrister who works weekends saw their working days shaded as time off.
  const workingDays = prefs.workingDays;
  const isNonWorking = (d: Date) => {
    const iso = d.getDay() === 0 ? 7 : d.getDay(); // JS Sunday=0 -> ISO Sunday=7
    return !workingDays.includes(iso);
  };

  // RC-DIARY Stage 1c — context preservation: remember where the barrister was
  // (month, selected day, and the open workspace) across navigation to a matter/
  // hearing and back. sessionStorage so it survives a route change and a refresh
  // within the tab, but resets in a fresh session.
  const [monthOffset, setMonthOffset] = useState(() => Number(sessionStorage.getItem('diary:monthOffset')) || 0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(
    () => sessionStorage.getItem('diary:selectedDay') || getTodayKey(),
  );
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [detailEvent, setDetailEvent] = useState<CalEvent | null>(null);
  const [editEvent, setEditEvent] = useState<CalEvent | null>(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const navigate = useNavigate();

  const store = useDiaryStore();
  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const user = useAuthStore((s) => s.user);
  // Pupils are READ-ONLY on the diary: calendar.routes.ts WRITE_ROLES excludes them
  // ("Pupils cannot modify the diary — they observe under supervision"), so every create/edit/
  // delete entry point below 403'd for them. One flag, consulted at all of them.
  const canWriteDiary_ = canWriteDiary(user?.memberType);
  const wsConnected = useWebSocketStore((s) => s.connected);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Polling + WebSocket subscription — re-attaches when socket connects/reconnects.
  useEffect(() => {
    const poll = setInterval(fetchEvents, 300_000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchEvents(); };
    document.addEventListener('visibilitychange', onVisible);
    const unsubWs = onCalendarUpdate(() => fetchEvents());
    return () => { clearInterval(poll); document.removeEventListener('visibilitychange', onVisible); unsubWs(); };
  }, [fetchEvents, wsConnected]);
  const myName = user?.lastName ?? '';

  // Persist "where I was" so returning from a matter/hearing lands on the same day/month.
  useEffect(() => {
    if (selectedDay) sessionStorage.setItem('diary:selectedDay', selectedDay);
  }, [selectedDay]);
  useEffect(() => {
    sessionStorage.setItem('diary:monthOffset', String(monthOffset));
  }, [monthOffset]);
  // Persist the open workspace so it re-expands on return; cleared when closed.
  useEffect(() => {
    if (detailEvent) sessionStorage.setItem('diary:openEvent', detailEvent.id);
    else sessionStorage.removeItem('diary:openEvent');
  }, [detailEvent]);
  // One-time restore: re-open the previously expanded workspace once events load.
  // Capture the id in a ref at first render — BEFORE the [detailEvent] effect above
  // (which runs on mount with detailEvent=null) can clear it. Avoids the race.
  const pendingOpenId = useRef<string | null>(sessionStorage.getItem('diary:openEvent'));
  const restoredWorkspace = useRef(false);
  useEffect(() => {
    if (restoredWorkspace.current) return;
    const id = pendingOpenId.current;
    if (!id) { restoredWorkspace.current = true; return; }
    const ev = store.events.find((e) => e.id === id);
    if (ev) { setDetailEvent(ev); restoredWorkspace.current = true; }
  }, [store.events]);

  /* ── Tasks due (for diary indicators) ── */
  const { data: tasksData } = useQuery({
    queryKey: ['diary-tasks', user?.id],
    queryFn: () => fetchTasks({ assignedToId: user?.id ?? '', limit: 100, status: 'pending' as any }),
    enabled: !!user?.id,
    staleTime: 60_000,
  });
  const tasksByDate = useMemo(() => {
    const map: Record<string, Task[]> = {};
    for (const t of tasksData?.tasks ?? []) {
      if (!t.dueDate) continue;
      const key = t.dueDate.slice(0, 10);
      (map[key] ??= []).push(t);
    }
    return map;
  }, [tasksData]);

  /* ── Month grid computation ── */
  const today = new Date();
  const todayKey = getTodayKey();

  const { viewDate, cells } = useMemo(() => {
    const y = today.getFullYear();
    const m = today.getMonth() + monthOffset;
    // Midday, not midnight. The label is formatted in the READER's timezone, so a local
    // midnight on the 1st falls into the previous month for anyone whose zone is behind the
    // machine's — the October grid was captioned "September 2026". Noon survives a shift of
    // up to twelve hours either way, which covers every real timezone.
    const vd = new Date(y, m, 1, 12);
    const startWeekday = (vd.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const c: Array<{ key: string; day: number; weekend: boolean } | null> = [];
    for (let i = 0; i < startWeekday; i++) c.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(y, m, d);
      const dow = dt.getDay();
      c.push({ key: dateKey(dt), day: d, weekend: dow === 0 || dow === 6 });
    }
    while (c.length % 7 !== 0) c.push(null);
    return { viewDate: vd, cells: c };
  }, [monthOffset]);

  /* ── Week view computation ── */
  const weekDays = useMemo<Date[]>(() => {
    const start = new Date();
    const monday = new Date(start);
    const dow = (start.getDay() + 6) % 7;
    monday.setDate(start.getDate() - dow + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const weekDayKeys = useMemo(() => weekDays.map(dateKey), [weekDays]);

  /** Day view: just selectedDay or today */
  const dayViewDate = useMemo(() => {
    if (selectedDay) return new Date(selectedDay + 'T00:00:00');
    return today;
  }, [selectedDay]);

  /** All events for week/day views, filtered to this barrister */
  const allEvents = useDiaryStore((s) => s.events);
  const myEvents = useMemo(() => allEvents.filter((e) => e.ownerId === user?.id), [allEvents, user?.id]);

  const weekEvents = useMemo(() => {
    return myEvents
      .map((e) => {
        const idx = weekDayKeys.indexOf(e.date);
        if (idx === -1) return null;
        return { ...e, dayIdx: idx };
      })
      .filter(Boolean) as (CalEvent & { dayIdx: number })[];
  }, [myEvents, weekDayKeys]);

  const dayEvents = useMemo(() => {
    const key = dateKey(dayViewDate);
    return myEvents.filter((e) => e.date === key);
  }, [myEvents, dayViewDate]);

  /* ── Side panel data — filtered to THIS barrister by UUID ── */
  const selectedEvents = selectedDay
    ? store.getEventsForDate(selectedDay).filter((e) => e.ownerId === user?.id)
    : [];
  const selectedStatus = selectedDay ? store.getStatus(selectedDay) : null;

  const selectedDateObj = selectedDay
    ? new Date(selectedDay + 'T00:00:00')
    : null;


  const handleToday = () => {
    setMonthOffset(0);
    setWeekOffset(0);
    setSelectedDay(todayKey);
  };

  const handlePrev = () => {
    if (view === 'month') setMonthOffset((o) => o - 1);
    else if (view === 'week') setWeekOffset((o) => o - 1);
    else setSelectedDay(dateKey(new Date(dayViewDate.getTime() - 86400000)));
  };

  const handleNext = () => {
    if (view === 'month') setMonthOffset((o) => o + 1);
    else if (view === 'week') setWeekOffset((o) => o + 1);
    else setSelectedDay(dateKey(new Date(dayViewDate.getTime() + 86400000)));
  };

  // The month view covers a month, so it is labelled with one. It used formatDate's 'long'
  // style, which always carries a day number, and so read "1 October 2026" for all of October.
  const viewLabel = view === 'month'
    ? formatMonthYear(viewDate)
    : view === 'week'
      ? `Week of ${formatDate(weekDays[0], { style: 'long' })}`
      : formatDate(dayViewDate, { style: 'long' });

  return (
    // Bound the page to the viewport so it never triggers a window scroll when
    // there is room — only the calendar body scrolls, and only when it must
    // (e.g. the tall week/day hour grid). The month view fits with no scrollbar.
    <div className="flex flex-col lg:h-[calc(100vh-96px)]">
      {/* Compact header — a single tight row, no oversized title block. */}
      {/* Wraps below the width where the title and both buttons fit on one line — at 320px
          ("Sync calendar" + "New event" against a 285px line) "New event" was 20px past the
          edge. `shrink-0` stays: in this COLUMN the main axis is vertical, so it is what stops
          the header being squeezed by the calendar below it. */}
      <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">My Diary</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" leftIcon={<ArrowsClockwise className="h-4 w-4" />} onClick={() => setSyncModalOpen(true)}>
            Sync calendar
          </Button>
          {canWriteDiary_ && (
            <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setNewEventOpen(true)}>
              New event
            </Button>
          )}
        </div>
      </div>

      {/* A failed load must not read as an empty diary ("No events scheduled"). Events already on
          screen are kept; the banner says they may be out of date. */}
      {store.error && (
        <div role="alert" className="mb-3 flex shrink-0 items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>{store.error} — what you see may be incomplete or out of date.</span>
          <Button variant="outline" size="sm" onClick={() => fetchEvents()}>Try again</Button>
        </div>
      )}

      {/* ── Toolbar ── */}
      <Card className="mb-3 shrink-0 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={handleToday}>Today</Button>
            <button onClick={handlePrev} className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
              <CaretLeft className="h-4 w-4" />
            </button>
            <button onClick={handleNext} className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground">
              <CaretRight className="h-4 w-4" />
            </button>
          </div>
          <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-semibold text-foreground sm:text-base">
            <CalendarBlank className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
            <span className="truncate">{viewLabel}</span>
          </div>
          <div className="flex w-full items-center gap-0.5 rounded-md border border-border bg-card p-0.5 text-xs sm:ml-auto sm:w-auto">
            {(['day', 'week', 'month'] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`flex-1 rounded-sm px-3 py-1.5 font-medium capitalize sm:flex-none ${
                  view === v ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Scrollable calendar body — only this scrolls, and only when the view is
          taller than the available space (week/day). Month view fits, no scroll. */}
      <div className="lg:min-h-0 lg:flex-1 lg:overflow-auto">
      {/* ── Month view ── */}
      {view === 'month' && (
        <div className="grid grid-cols-1 gap-6 lg:h-full lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_320px]">
          <Card className="lg:flex lg:min-h-0 lg:flex-col">

          <CardContent className="flex flex-col p-1.5 sm:p-3 lg:flex-1">
            {/* Day headers */}
            <div className="mb-2 grid shrink-0 grid-cols-7 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {DAYS_HEADER.map((d) => <div key={d} className="py-1">{d}</div>)}
            </div>

            {/* Day cells — on desktop the rows share the available height so the
                whole month fits without a scroll and cells carry no wasted space. */}
            <div
              className="grid grid-cols-7 gap-0.5 sm:gap-1 lg:flex-1"
              style={{ gridTemplateRows: `repeat(${cells.length / 7}, minmax(0, 1fr))` }}
            >
              {cells.map((cell, i) => {
                if (!cell) return <div key={i} />;
                const isToday = cell.key === todayKey;
                const isSelected = cell.key === selectedDay;
                const status = store.getStatus(cell.key);
                const dayEvents = store.getEventsForDate(cell.key).filter((e) => e.ownerId === user?.id);
                const dayTaskCount = tasksByDate[cell.key]?.length ?? 0;
                const maxDots = 4;
                const overflow = dayEvents.length - maxDots;

                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedDay(cell.key)}
                    className={[
                      'flex h-full min-h-0 flex-col rounded-md border p-1 text-left transition sm:p-1.5',
                      status ? AVAIL_TINT[status] : 'bg-card',
                      cell.weekend ? 'opacity-70' : '',
                      isSelected ? 'border-brand-600 ring-1 ring-brand-300' : 'border-transparent',
                      isToday ? 'ring-2 ring-inset ring-brand-600' : '',
                      'hover:border-brand-300',
                    ].join(' ')}
                  >
                    <span className={`text-xs font-semibold tabular ${isToday ? 'text-brand-700 dark:text-brand-300' : 'text-foreground'}`}>
                      {cell.day}
                    </span>
                    {(dayEvents.length > 0 || dayTaskCount > 0) && (
                      <div className="mt-auto flex flex-wrap items-center gap-0.5">
                        {dayEvents.slice(0, maxDots).map((ev) => (
                          <span key={ev.id} className={`h-2 w-2 rounded-full ${TONE_DOT[ev.tone]}`} />
                        ))}
                        {overflow > 0 && (
                          <span className="text-[8px] font-semibold text-muted-foreground">+{overflow}</span>
                        )}
                        {dayTaskCount > 0 && (
                          <span className="ml-auto rounded bg-amber-100 dark:bg-amber-500/15 px-1 py-0.5 text-[8px] font-bold text-amber-700 dark:text-amber-300">
                            {dayTaskCount}T
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* ── Side panel — scrolls on its own so a busy day never pushes the
            calendar (and its day headers) out of view ── */}
        <div className="space-y-4 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:pr-1">
          <Card>
            <CardContent className="p-5">
              {selectedDateObj ? (
                <>
                  <div className="text-sm font-semibold text-foreground">
                    {formatDate(selectedDateObj, { style: 'long' })}
                  </div>
                  {selectedStatus && (
                    <Badge className={`mt-2 ${AVAIL_STYLE[selectedStatus]}`}>{AVAIL_LABEL[selectedStatus]}</Badge>
                  )}

                  {selectedEvents.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {selectedEvents.map((ev) => {
                        const Icon = KIND_ICON[ev.kind];
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={() => setDetailEvent(ev)}
                            className={`flex w-full items-start gap-2.5 rounded-md border p-3 text-left transition hover:shadow-sm ${
                              ev.status === 'tentative' ? 'border-dashed border-amber-300 dark:border-amber-500/40 bg-amber-50/50 dark:bg-amber-500/10 text-amber-900 dark:text-amber-200' : TONE_STYLE[ev.tone]
                            }`}
                          >
                            <div className={`mt-0.5 h-full w-1 shrink-0 rounded-full ${ev.status === 'tentative' ? 'bg-amber-400' : TONE_DOT[ev.tone]}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <Icon className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate text-sm font-semibold">{ev.title}</span>
                                {ev.status && (
                                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${EVENT_STATUS_BADGE[ev.status]}`}>
                                    {EVENT_STATUS_LABEL[ev.status]}
                                  </span>
                                )}
                              </div>
                              <div className="mt-0.5 text-xs opacity-80">
                                {formatWallTime(eventStartLabel(ev))} — {formatWallTime(eventEndLabel(ev))}
                              </div>
                              {/* Matter context (RC-DIARY Stage 1) — the card now answers
                                  "what have I got on" without opening the matter. */}
                              {ev.matter ? (
                                <div className="mt-1 space-y-0.5">
                                  {/* Card title already carries the matter number (auto-generated
                                      "type — number"), so show only the case NAME here — no repeat.
                                      Also suppress it when the event title IS the matter name (e.g. a
                                      meeting the barrister named after the case) — otherwise the same
                                      line prints twice. */}
                                  {(() => {
                                    const matterLabel = ev.matter.name || ev.matter.number;
                                    const isRepeatOfTitle =
                                      !!matterLabel &&
                                      matterLabel.trim().toLowerCase() === ev.title.trim().toLowerCase();
                                    return matterLabel && !isRepeatOfTitle ? (
                                      <div className="truncate text-xs font-medium opacity-90">
                                        {matterLabel}
                                      </div>
                                    ) : null;
                                  })()}
                                  {(ev.hearingType || ev.matter.client || ev.matter.court || ev.judge || ev.matter.opponent) && (
                                    <div className="truncate text-[11px] capitalize opacity-70">
                                      {/* Ordered by glance value so the first token survives
                                          truncation on mobile (single line): type → client →
                                          court → judge → opponent. */}
                                      {[
                                        ev.hearingType,
                                        ev.matter.client,
                                        ev.matter.court,
                                        ev.judge,
                                        ev.matter.opponent && `v ${ev.matter.opponent}`,
                                      ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                ev.sub && <div className="mt-0.5 truncate text-xs opacity-70">{ev.sub}</div>
                              )}
                              {/* Suppress the raw location when the matter's court is already
                                  shown above (hearings usually duplicate it); personal events
                                  with no matter still show their location. */}
                              {ev.location && ev.location !== ev.sub && !ev.matter?.court && (
                                <div className="mt-0.5 truncate text-xs opacity-70">{ev.location}</div>
                              )}
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <Badge className="text-[9px]">{KIND_LABEL[ev.kind]}</Badge>
                                {/* RC-STAGE3 — canonical readiness / lifecycle / blocked, from /calendar. */}
                                {ev.kind === 'hearing' && ev.lifecycle && !['Scheduled', 'Done'].includes(ev.lifecycle) && (
                                  <Badge variant="info" className="text-[9px] capitalize">{ev.lifecycle}</Badge>
                                )}
                                {ev.kind === 'hearing' && typeof ev.readinessPct === 'number' && ev.readinessPct < 100 && (
                                  <Badge variant={ev.readinessPct < 50 ? 'danger' : 'warning'} className="text-[9px]">{ev.readinessPct}%</Badge>
                                )}
                                {(ev.obligationsBlocked ?? 0) > 0 && (
                                  <Badge variant="danger" className="text-[9px]">{ev.obligationsBlocked} blocked</Badge>
                                )}
                                {(ev.matter?.priority === 'high' || ev.matter?.priority === 'urgent') && (
                                  <Badge
                                    variant={ev.matter.priority === 'urgent' ? 'danger' : 'warning'}
                                    className="text-[9px] capitalize"
                                  >
                                    {ev.matter.priority}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mt-6 text-center text-sm text-muted-foreground">
                      <CalendarBlank className="mx-auto mb-2 h-8 w-8 opacity-40" />
                      No events scheduled
                    </div>
                  )}

                  {/* Tasks due on selected day */}
                  {selectedDay && (tasksByDate[selectedDay]?.length ?? 0) > 0 && (
                    <div className="mt-4 border-t border-border pt-3">
                      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Tasks due
                      </div>
                      <div className="space-y-1.5">
                        {tasksByDate[selectedDay]!.map((t) => (
                          <button
                            key={t.id}
                            className="flex w-full items-center gap-2 rounded-lg border border-amber-100 bg-amber-50/60 dark:bg-amber-500/10 px-2.5 py-2 text-left transition-colors hover:bg-amber-100 dark:hover:bg-amber-500/20/70 dark:bg-amber-500/15 hover:border-amber-200 dark:border-amber-500/30"
                            onClick={() => setSelectedTask(t)}
                          >
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                            <div className="flex-1 min-w-0">
                              <div className="truncate text-xs font-medium text-foreground">{t.title}</div>
                              {t.caseId && (
                                <div className="text-[10px] text-muted-foreground">
                                  {(t as any).case?.caseNumber ?? ''}
                                </div>
                              )}
                            </div>
                            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${
                              t.priority === 'high' || t.priority === 'urgent'
                                ? 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {t.priority}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {canWriteDiary_ && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4 w-full"
                      leftIcon={<Plus className="h-3.5 w-3.5" />}
                      onClick={() => setNewEventOpen(true)}
                    >
                      Add event
                    </Button>
                  )}
                </>
              ) : (
                <div className="text-center text-sm text-muted-foreground">Select a day to view details</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      )}

      {/* ── Week view ── */}
      {view === 'week' && (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-[44px_repeat(7,minmax(0,1fr))] border-b border-border text-xs sm:grid-cols-[60px_repeat(7,minmax(0,1fr))]">
              <div className="border-r border-border bg-muted/30 px-3 py-3" />
              {weekDays.map((d) => {
                const isToday = d.toDateString() === today.toDateString();
                const isWeekend = isNonWorking(d);
                return (
                  <div
                    key={d.toISOString()}
                    className={`border-r border-border px-3 py-3 last:border-r-0 ${isToday ? 'bg-brand-50' : isWeekend ? 'bg-muted/20' : 'bg-muted/30'}`}
                  >
                    <div className={`text-[10px] font-semibold uppercase tracking-wider ${isToday ? 'text-brand-700 dark:text-brand-300' : 'text-muted-foreground'}`}>
                      {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                    </div>
                    <div className={`tabular mt-0.5 text-base font-semibold ${isToday ? 'text-brand-700 dark:text-brand-300' : 'text-foreground'}`}>
                      {d.getDate()}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-[44px_repeat(7,minmax(0,1fr))] sm:grid-cols-[60px_repeat(7,minmax(0,1fr))]">
              {/* Hours column */}
              <div className="border-r border-border">
                {HOURS.map((h) => (
                  <div key={h} className="flex items-start justify-end border-b border-border px-1 pt-1 text-[10px] sm:px-2 text-muted-foreground" style={{ height: HOUR_PX }}>
                    {h}
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map((d, dayIdx) => {
                const isWeekend = isNonWorking(d);
                return (
                  <div key={dayIdx} className={`relative border-r border-border last:border-r-0 ${isWeekend ? 'bg-muted/10' : ''}`}>
                    {HOURS.map((_h, hourIdx) => (canWriteDiary_ ? (
                      <button
                        key={hourIdx}
                        type="button"
                        onClick={() => { setSelectedDay(dateKey(d)); setNewEventOpen(true); }}
                        className="block w-full border-b border-border transition hover:bg-brand-50/40 focus:bg-brand-50/40 focus:outline-none"
                        style={{ height: HOUR_PX }}
                        aria-label={`Add event at ${HOURS[hourIdx]}`}
                      />
                    ) : (
                      // Read-only diary (pupil): keep the hour grid, drop the "add event"
                      // affordance — a focusable button that 403s is worse than no button.
                      <div
                        key={hourIdx}
                        className="block w-full border-b border-border"
                        style={{ height: HOUR_PX }}
                      />
                    )))}

                    {/* Event blocks */}
                    {weekEvents.filter((e) => e.dayIdx === dayIdx).map((e) => {
                      const Icon = KIND_ICON[e.kind];
                      const isCancelled = e.status === 'cancelled';
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={(ev) => { ev.stopPropagation(); setDetailEvent(e); }}
                          title={isCancelled ? `${e.title} — vacated` : e.title}
                          aria-label={isCancelled ? `${e.title} — vacated` : e.title}
                          className={`absolute inset-x-0.5 cursor-pointer overflow-hidden rounded-md border px-1 py-1 text-left text-[10px] shadow-xs transition focus:outline-none focus:ring-2 focus:ring-brand-200 sm:px-1.5 ${
                            isCancelled
                              ? 'border-red-300 bg-muted/40 opacity-60 hover:opacity-80 dark:border-red-500/40'
                              : `hover:shadow-card ${TONE_HOVER[e.tone]}`
                          }`}
                          style={blockBox(e.start, e.span)}
                        >
                          <div className="flex items-center gap-1">
                            <Icon className="hidden h-3 w-3 shrink-0 sm:block" />
                            <span className={`truncate font-semibold ${isCancelled ? 'line-through' : ''}`}>{e.title}</span>
                          </div>
                          {/* A vacated hearing says so at every width — the strike-through and
                              the red border alone are too easy to miss. The letter-spacing is
                              what made it overflow a narrow column, so that goes rather than
                              the word. */}
                          {isCancelled && <div className="mt-0.5 truncate text-[8px] font-bold uppercase tracking-normal text-red-600 dark:text-red-400 sm:text-[9px] sm:tracking-wider">Vacated</div>}
                          {!isCancelled && e.span >= 2 && e.sub && <div className="mt-0.5 hidden truncate opacity-80 sm:block">{e.sub}</div>}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Day view ── */}
      {view === 'day' && (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-[60px_minmax(0,1fr)] border-b border-border">
              <div className="border-r border-border bg-muted/30 px-3 py-4" />
              <div className="bg-brand-50 px-4 py-4">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-700 dark:text-brand-300">
                  {dayViewDate.toLocaleDateString('en-GB', { weekday: 'long' })}
                </div>
                <div className="tabular mt-0.5 text-xl font-bold text-brand-700 dark:text-brand-300">
                  {formatDate(dayViewDate, { style: 'long' })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[60px_minmax(0,1fr)]">
              {/* Hours column */}
              <div className="border-r border-border">
                {HOURS.map((h) => (
                  <div key={h} className="flex items-start justify-end border-b border-border px-2 pt-1 text-[10px] text-muted-foreground" style={{ height: HOUR_PX }}>
                    {h}
                  </div>
                ))}
              </div>

              {/* Day column */}
              <div className="relative">
                {HOURS.map((_h, hourIdx) => (canWriteDiary_ ? (
                  <button
                    key={hourIdx}
                    type="button"
                    onClick={() => setNewEventOpen(true)}
                    className="block w-full border-b border-border transition hover:bg-brand-50/40 focus:bg-brand-50/40 focus:outline-none"
                    style={{ height: HOUR_PX }}
                    aria-label={`Add event at ${HOURS[hourIdx]}`}
                  />
                ) : (
                  // Read-only diary (pupil) — see the week grid above.
                  <div
                    key={hourIdx}
                    className="block w-full border-b border-border"
                    style={{ height: HOUR_PX }}
                  />
                )))}

                {/* Event blocks */}
                {dayEvents.map((e) => {
                  const Icon = KIND_ICON[e.kind];
                  const isCancelled = e.status === 'cancelled';
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); setDetailEvent(e); }}
                      className={`absolute inset-x-2 cursor-pointer overflow-hidden rounded-md border px-3 py-2 text-left text-xs shadow-xs transition focus:outline-none focus:ring-2 focus:ring-brand-200 ${
                        isCancelled
                          ? 'border-border bg-muted/40 opacity-50 hover:opacity-70'
                          : `hover:shadow-card ${TONE_HOVER[e.tone]}`
                      }`}
                      style={blockBox(e.start, e.span)}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className={`truncate font-semibold ${isCancelled ? 'line-through' : ''}`}>{e.title}</span>
                        {isCancelled && <span className="ml-1 rounded bg-red-100 dark:bg-red-500/15 px-1 py-0.5 text-[9px] font-bold uppercase text-red-700 dark:text-red-300">Vacated</span>}
                      </div>
                      <div className="mt-1 text-[10px] opacity-80">{eventStartLabel(e)} — {eventEndLabel(e)}</div>
                      {!isCancelled && e.sub && <div className="mt-1 truncate opacity-80">{e.sub}</div>}
                      {/* `sub` is already location-or-case; only show location when it adds something new. */}
                      {e.location && e.location !== e.sub && <div className="mt-0.5 truncate opacity-70">{e.location}</div>}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      </div>

      {/* ── Legend — pinned below the calendar, always visible, in a tidy bar ── */}
      <div className="mt-3 flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-border bg-card px-4 py-2 text-xs text-muted-foreground">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">Events</span>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-brand-500" /> Hearings</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" /> Conferences</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Brief returns</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-violet-500" /> Tasks</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" /> Personal</div>
        <span className="mx-1 h-4 w-px bg-border" aria-hidden />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">Availability</span>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-emerald-200" /> Available</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-amber-200" /> Partial</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-brand-200" /> Booked</div>
        <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded bg-slate-300" /> On leave</div>
      </div>

      {/* ── Modals ── */}
      {/* Belt and braces: every trigger above is already withheld from a read-only viewer, but the
          modals gate too so no other path can open a form the API will refuse. */}
      {newEventOpen && canWriteDiary_ && (
        <NewEventModal
          defaultDate={selectedDay ?? todayKey}
          onCancel={() => setNewEventOpen(false)}
          onSave={async (ev) => {
            // AWAIT before claiming anything. `addEvent` inserts optimistically, calls the API,
            // and on failure rolls the event back and RETHROWS. This handler used to ignore the
            // promise: the success toast had already fired and the modal had already closed, so
            // a refused event simply vanished from the diary with nothing on screen to explain
            // it — and the rejection went unhandled. A barrister was told a hearing was in their
            // diary when it was not.
            try {
              await store.addEvent({ ...ev, ownerId: user?.id, barrister: myName, createdBy: myName });
            } catch (err) {
              toast.error(getApiErrorMessage(err, 'Could not add the event — please try again'));
              return; // modal stays open with their input intact
            }
            setNewEventOpen(false);
            setSelectedDay(ev.date);
            toast.success('Event added');
          }}
        />
      )}

      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onDelete={!canWriteDiary_ ? undefined : () => {
            store.deleteEvent(detailEvent.id);
            setDetailEvent(null);
            toast.success('Event deleted');
          }}
          onEdit={!canWriteDiary_ ? undefined : () => {
            setEditEvent(detailEvent);
            setDetailEvent(null);
          }}
          onOpenCase={() => navigate(`/barrister/matters/${detailEvent.caseId}`)}
          onOpenHearing={() => navigate(`/barrister/hearings/${detailEvent.hearingId}`)}
        />
      )}

      {editEvent && canWriteDiary_ && (
        <EditEventModal
          event={editEvent}
          onCancel={() => setEditEvent(null)}
          onSave={async (updated) => {
            // Honest feedback: only claim success once the SERVER confirms. Previously the
            // success toast fired optimistically, so a failed save (validation, 500, offline)
            // still showed "Event updated" — a false success. updateEvent throws on failure.
            try {
              await store.updateEvent(editEvent.id, updated);
              setEditEvent(null);
              toast.success('Event updated');
            } catch {
              toast.error('Could not save the change — please try again');
            }
          }}
        />
      )}

      {syncModalOpen && <CalendarSyncModal onClose={() => setSyncModalOpen(false)} />}

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onOpenCase={() => { setSelectedTask(null); navigate(`/barrister/matters/${selectedTask.caseId}`); }}
          onOpenHearing={() => { setSelectedTask(null); navigate(`/barrister/hearings/${selectedTask.hearingId}`); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* New event modal                                                */
/* ═══════════════════════════════════════════════════════════════ */

function NewEventModal({
  defaultDate,
  onCancel,
  onSave,
}: {
  defaultDate: string;
  onCancel: () => void;
  onSave: (e: CalEvent) => void;
}) {
  const [form, setForm] = useState({
    title: '',
    kind: 'personal' as EventKind,
    status: 'scheduled' as EventStatus,
    date: defaultDate,
    startTime: '', // blank by default — user must pick a time (no misleading pre-filled/past time)
    span: 1,
    location: '',
    notes: '',
    caseId: '', // optional link to a matter — meeting then shows on that matter's timeline
  });
  const [titleTouched, setTitleTouched] = useState(false);

  const userId = useAuthStore((s) => s.user?.id ?? null);
  const barristerId = useAuthStore((s) => s.user?.barristerId ?? null);

  // Clerk coordination (NOTIFICATION-DESIGN.md — explicit active request). The barrister may ask
  // specific supporting clerk(s) to coordinate a matter-linked meeting; those clerks are notified
  // on save. The list is the barrister's OWN supporting clerks — the server re-validates eligibility.
  const [requiresCoordination, setRequiresCoordination] = useState(false);
  const [coordinationClerkIds, setCoordinationClerkIds] = useState<string[]>([]);
  const [supportingClerks, setSupportingClerks] = useState<ClerkAssignment[]>([]);
  useEffect(() => {
    if (!barristerId) return;
    let live = true;
    fetchClerksForBarrister(barristerId)
      .then((rows) => { if (live) setSupportingClerks(rows.filter((r) => r.isActive)); })
      .catch(() => { if (live) setSupportingClerks([]); });
    return () => { live = false; };
  }, [barristerId]);
  const toggleClerk = (clerkId: string) =>
    setCoordinationClerkIds((ids) => ids.includes(clerkId) ? ids.filter((i) => i !== clerkId) : [...ids, clerkId]);

  // Optional "Related matter" picker — mirrors the timer's matter selector so a
  // meeting can be tied to a matter and reflected on that matter's Timeline.
  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  useEffect(() => { fetchMatters(); }, [fetchMatters]);
  const matterOptions = useMemo(
    () => matters.filter((m) => isOpenMatterStatus(m.status)).map((m) => ({ id: m.id, label: `${m.number} · ${m.name}` })),
    [matters],
  );

  // Exact start time → ISO for conflict detection.
  const startIso = useMemo(() => {
    if (!form.date || !form.startTime) return null;
    // UTC-wall-clock (with Z) to match how every event is stored & displayed
    // (diaryStore.buildStartAt + apiToCalEvent). Without the Z this parsed as LOCAL
    // time and shifted the proposed slot by the viewer's TZ offset, so it never
    // overlapped a hearing at the same displayed time → the clash warning never fired.
    return new Date(`${form.date}T${form.startTime}:00Z`).toISOString();
  }, [form.date, form.startTime]);

  const { getConflicts, getEventsForDate } = useBarristerBusy(userId, startIso ? [startIso] : []);
  const conflicts = useMemo(
    () => (startIso ? getConflicts(startIso, form.span * 60) : []),
    [startIso, form.span, getConflicts],
  );
  const dayEvents = useMemo(
    () => (startIso ? getEventsForDate(startIso) : []),
    [startIso, getEventsForDate],
  );

  // Block past date/times — the chosen date+time must be now or later.
  const notPast = !!startIso && new Date(startIso).getTime() >= Date.now() - 60_000;
  // Coordination is only offered on matter-linked events with at least one supporting clerk.
  const canCoordinate = !!form.caseId && supportingClerks.length > 0;
  // If the barrister turns coordination on, they must make an explicit routing decision.
  const coordinationValid = !(canCoordinate && requiresCoordination) || coordinationClerkIds.length > 0;
  const valid = form.title.trim().length > 0 && notPast && coordinationValid;

  // Double-booking: overlaps warn-and-confirm (never silently save, never hard-block).
  const overlaps = useMemo(() => conflicts.filter((c) => c.kind === 'overlap'), [conflicts]);
  const [confirmClash, setConfirmClash] = useState(false);

  const buildEvent = (): CalEvent => ({
    id: nextEventId(),
    date: form.date,
    title: form.title.trim(),
    location: form.location.trim() || undefined,
    notes: form.notes.trim() || undefined,
    kind: form.kind,
    status: form.status,
    tone: KIND_TONE[form.kind],
    start: timeToSlotIndex(form.startTime),
    startTime: form.startTime,
    span: form.span,
    caseId: form.caseId || undefined, // links the event to a matter (optional)
    coordinationClerkIds:
      canCoordinate && requiresCoordination && coordinationClerkIds.length > 0
        ? coordinationClerkIds
        : undefined,
  });
  const attemptSave = () => {
    if (overlaps.length > 0) { setConfirmClash(true); return; }
    onSave(buildEvent());
  };
  const clashItems: ClashItem[] = overlaps.map((c) => ({
    title: c.event.title, startAt: c.event.startAt, endAt: c.event.endAt,
  }));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-lg shadow-pop">
        <div className="flex items-center justify-between border-b border-border px-6 py-3">
          <div>
            <div className="text-base font-semibold text-foreground">New event</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{form.date}{form.startTime ? ` · ${form.startTime}` : ''}</div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onCancel}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-3 p-5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title *</label>
            <input
              type="text"
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              onBlur={() => setTitleTouched(true)}
              className={`h-10 w-full rounded-md border bg-card px-3 text-sm focus:outline-none focus:ring-2 ${
                titleTouched && !form.title.trim()
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
                  : 'border-border focus:border-brand-600 focus:ring-brand-100'
              }`}
              placeholder="e.g. R v Whitfield — PTPH"
            />
            {titleTouched && !form.title.trim() && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">Title is required.</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Related matter <span className="font-normal normal-case text-muted-foreground/60">(optional)</span>
            </label>
            <select
              value={form.caseId}
              onChange={(e) => setForm({ ...form, caseId: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            >
              <option value="">— No matter (personal event) —</option>
              {matterOptions.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            {form.caseId && (
              <p className="mt-1 text-[11px] text-muted-foreground">This meeting will appear on the matter's timeline.</p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
            <div className="grid grid-cols-4 gap-1.5">
              {CREATABLE_KINDS.map((k) => {
                const Icon = KIND_ICON[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k })}
                    className={`flex flex-col items-center gap-1 rounded-md border p-2 transition ${
                      form.kind === k ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${form.kind === k ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'}`} />
                    <span className="text-[10px] font-semibold text-foreground">{KIND_LABEL[k]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as EventStatus })}
              className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            >
              {EVENT_STATUSES.map((s) => <option key={s} value={s}>{EVENT_STATUS_LABEL[s]}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</label>
              <input
                type="date"
                value={form.date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Start</label>
              <input
                type="time"
                step={60}
                value={form.startTime}
                min={form.date === todayLocalDate() ? nowLocalTime() : undefined}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hours</label>
              <select
                value={form.span}
                onChange={(e) => setForm({ ...form, span: parseInt(e.target.value, 10) })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} hr</option>)}
              </select>
            </div>
          </div>

          {/* Schedule conflict check */}
          {startIso && (
            <BarristerDayTimeline
              events={dayEvents}
              proposedStart={startIso}
              proposedDurationMinutes={form.span * 60}
              conflicts={conflicts}
              utc
              proposedLabel={KIND_LABEL[form.kind]}
            />
          )}
          {startIso && !notPast && (
            <p className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
              <X className="h-3.5 w-3.5" /> You can't select a past time — pick the current time or later.
            </p>
          )}
          {startIso && notPast && overlaps.length > 0 && (
            <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
              <Warning className="h-3.5 w-3.5" weight="fill" /> You're already booked at this time — you'll be asked to confirm.
            </p>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              placeholder="e.g. Court 4, Royal Courts of Justice"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</label>
            <ExpandableTextarea
              label="Notes"
              rows={2}
              value={form.notes}
              onChange={(v) => setForm({ ...form, notes: v })}
              placeholder="Anything you want to remember"
            />
          </div>

          {/* Clerk coordination — a distinct operational request, kept visually separate from the
              schedule-conflict warning above. Only offered on a matter-linked event that has
              supporting clerks. Turning it on requires an explicit routing choice (see validation). */}
          {canCoordinate && (
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  checked={requiresCoordination}
                  onChange={(e) => setRequiresCoordination(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
                />
                <span>
                  <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Clerk coordination</span>
                  <span className="block text-sm text-foreground">Requires clerk coordination</span>
                </span>
              </label>

              {requiresCoordination && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs font-semibold text-foreground">Notify clerk(s)</div>
                  <div className="space-y-1">
                    {supportingClerks.map((c) => {
                      const selected = coordinationClerkIds.includes(c.clerkId);
                      return (
                        <label
                          key={c.clerkId}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/40"
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleClerk(c.clerkId)}
                            className="h-4 w-4 rounded border-border text-brand-600 dark:text-brand-400 focus:ring-brand-100"
                          />
                          <span className="text-foreground">{c.clerk.firstName} {c.clerk.lastName}</span>
                        </label>
                      );
                    })}
                  </div>
                  {coordinationClerkIds.length > 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      These clerks will receive a notification when the meeting is saved.
                    </p>
                  ) : (
                    <p className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                      <Warning className="h-3.5 w-3.5" weight="fill" /> Select at least one clerk to notify.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" size="md" onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            size="md"
            disabled={!valid}
            onClick={attemptSave}
          >
            Save event
          </Button>
        </div>
      </Card>
      </div>
      {confirmClash && (
        <DoubleBookConfirm
          clashes={clashItems}
          onCancel={() => setConfirmClash(false)}
          onConfirm={() => { setConfirmClash(false); onSave(buildEvent()); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* Edit event modal                                                */
/* ═══════════════════════════════════════════════════════════════ */

function EditEventModal({
  event,
  onCancel,
  onSave,
}: {
  event: CalEvent;
  onCancel: () => void;
  onSave: (updates: Partial<CalEvent>) => void;
}) {
  const [form, setForm] = useState({
    title: event.title,
    kind: event.kind,
    status: (event.status ?? 'scheduled') as EventStatus,
    date: event.date,
    startTime: eventStartLabel(event),
    span: event.span,
    location: event.location ?? '',
    notes: event.notes ?? '',
  });

  const userId = useAuthStore((s) => s.user?.id ?? null);

  const startIso = useMemo(() => {
    if (!form.date || !form.startTime) return null;
    // UTC-wall-clock (with Z) to match how every event is stored & displayed
    // (diaryStore.buildStartAt + apiToCalEvent). Without the Z this parsed as LOCAL
    // time and shifted the proposed slot by the viewer's TZ offset, so it never
    // overlapped a hearing at the same displayed time → the clash warning never fired.
    return new Date(`${form.date}T${form.startTime}:00Z`).toISOString();
  }, [form.date, form.startTime]);

  const { getConflicts, getEventsForDate } = useBarristerBusy(userId, startIso ? [startIso] : []);
  // Exclude the event being edited so it doesn't flag itself as a conflict
  const conflicts = useMemo(
    () => (startIso ? getConflicts(startIso, form.span * 60).filter((c) => c.event.id !== event.id) : []),
    [startIso, form.span, getConflicts, event.id],
  );
  const dayEvents = useMemo(
    () => (startIso ? getEventsForDate(startIso).filter((e) => e.id !== event.id) : []),
    [startIso, getEventsForDate, event.id],
  );

  const valid = form.title.trim().length > 0;

  // Double-booking: rescheduling onto an existing event warns-and-confirms.
  const overlaps = useMemo(() => conflicts.filter((c) => c.kind === 'overlap'), [conflicts]);
  const [confirmClash, setConfirmClash] = useState(false);

  const buildUpdates = (): Partial<CalEvent> => ({
    date: form.date,
    title: form.title.trim(),
    location: form.location.trim() || undefined,
    notes: form.notes.trim() || undefined,
    kind: form.kind,
    status: form.status,
    tone: KIND_TONE[form.kind],
    start: timeToSlotIndex(form.startTime),
    startTime: form.startTime,
    span: form.span,
  });
  const attemptSave = () => {
    if (overlaps.length > 0) { setConfirmClash(true); return; }
    onSave(buildUpdates());
  };
  const clashItems: ClashItem[] = overlaps.map((c) => ({
    title: c.event.title, startAt: c.event.startAt, endAt: c.event.endAt,
  }));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="w-full max-w-lg shadow-pop">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="text-base font-semibold text-foreground">Edit event</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{form.date}{form.startTime ? ` · ${form.startTime}` : ''}</div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onCancel}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="space-y-3 p-5">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Title *</label>
            <input
              type="text"
              autoFocus
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</label>
            <div className="grid grid-cols-4 gap-1.5">
              {CREATABLE_KINDS.map((k) => {
                const Icon = KIND_ICON[k];
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm({ ...form, kind: k })}
                    className={`flex flex-col items-center gap-1 rounded-md border p-2 transition ${
                      form.kind === k ? 'border-brand-600 bg-brand-50' : 'border-border hover:border-brand-200 hover:bg-muted/40'
                    }`}
                  >
                    <Icon className={`h-4 w-4 ${form.kind === k ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'}`} />
                    <span className="text-[10px] font-semibold text-foreground">{KIND_LABEL[k]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as EventStatus })}
              className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            >
              {EVENT_STATUSES.map((s) => <option key={s} value={s}>{EVENT_STATUS_LABEL[s]}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Start</label>
              <input
                type="time"
                step={60}
                value={form.startTime}
                min={form.date === todayLocalDate() ? nowLocalTime() : undefined}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Hours</label>
              <select
                value={form.span}
                onChange={(e) => setForm({ ...form, span: parseInt(e.target.value, 10) })}
                className="h-10 w-full rounded-md border border-border bg-card px-2 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} hr</option>)}
              </select>
            </div>
          </div>

          {/* Schedule conflict check */}
          {startIso && (
            <BarristerDayTimeline
              events={dayEvents}
              proposedStart={startIso}
              proposedDurationMinutes={form.span * 60}
              conflicts={conflicts}
              utc
              proposedLabel={KIND_LABEL[form.kind]}
            />
          )}
          {startIso && overlaps.length > 0 && (
            <p className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
              <Warning className="h-3.5 w-3.5" weight="fill" /> You're already booked at this time — you'll be asked to confirm.
            </p>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Location</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</label>
            <ExpandableTextarea
              label="Notes"
              rows={3}
              value={form.notes}
              onChange={(v) => setForm({ ...form, notes: v })}
            />
          </div>
        </CardContent>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" size="md" onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            size="md"
            disabled={!valid}
            onClick={attemptSave}
          >
            Save changes
          </Button>
        </div>
      </Card>
      </div>
      {confirmClash && (
        <DoubleBookConfirm
          clashes={clashItems}
          onCancel={() => setConfirmClash(false)}
          onConfirm={() => { setConfirmClash(false); onSave(buildUpdates()); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* Event detail modal                                             */
/* ═══════════════════════════════════════════════════════════════ */

function EventDetailModal({
  event,
  onClose,
  onDelete,
  onEdit,
  onOpenCase,
  onOpenHearing,
}: {
  event: CalEvent;
  onClose: () => void;
  /** Omit to render no Delete control — a read-only viewer (pupil). */
  onDelete?: () => void;
  /** Omit to render no Edit control — a read-only viewer (pupil). */
  onEdit?: () => void;
  onOpenCase: () => void;
  onOpenHearing: () => void;
}) {
  const navigate = useNavigate();
  const Icon = KIND_ICON[event.kind];
  const dateObj = new Date(event.date + 'T00:00:00');

  // Cross-reference hearing store for live operational status
  const hearing = useHearingsStore((s) =>
    event.hearingId ? s.hearings.find((h) => h.id === event.hearingId) : undefined
  );
  const statusCfg = hearing ? getStatusConfig(hearing.status) : null;
  const isPastHearing = event.kind === 'hearing' && event.date < getTodayKey();
  const needsOutcome = isPastHearing && hearing && !hearing.outcome && !['completed','vacated','disposed','adjourned'].includes(hearing.status);
  const isToday = event.kind === 'hearing' && event.date === getTodayKey();

  // RC-DIARY Stage 1b: lazily load the workspace payload only when this event has a
  // matter/hearing (Tier-2 of the contract — never fetched for the card list).
  const isWorkspace = !!(event.caseId || event.hearingId);
  const { data: detail } = useQuery({
    queryKey: ['diary-detail', event.id],
    queryFn: () => fetchDiaryItemDetail(event.id),
    enabled: isWorkspace,
    staleTime: 60_000,
  });

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-2 sm:p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className={`w-full ${isWorkspace ? 'max-w-2xl' : 'max-w-md'} flex max-h-[92vh] flex-col overflow-hidden shadow-pop sm:max-h-[88vh]`} onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-start justify-between border-b border-border bg-brand-50 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-card">
              <Icon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
            </div>
            {/* RC-DIARY Stage 1c — one clean header: each fact appears ONCE.
                With a matter, lead with the case NAME + type + number (drop the
                auto-generated "type — number" title and the repeated sub). */}
            <div>
              {detail?.matter ? (
                <>
                  <Badge className="capitalize">
                    {(detail.hearing?.type ?? '').replace(/_/g, ' ') || KIND_LABEL[event.kind]}
                  </Badge>
                  <div className="mt-1 text-base font-semibold text-foreground">{detail.matter.name}</div>
                  <div className="mt-0.5 text-xs font-medium text-brand-600 dark:text-brand-400">{detail.matter.number}</div>
                </>
              ) : (
                <>
                  <Badge>{KIND_LABEL[event.kind]}</Badge>
                  <div className="mt-1 text-base font-semibold text-foreground">{event.title}</div>
                  {event.sub && <div className="mt-0.5 text-xs text-muted-foreground">{event.sub}</div>}
                </>
              )}
            </div>
          </div>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3 sm:space-y-2.5 sm:p-5">
          {/* Date and time on ONE line — they were stacked, costing a row for six words. */}
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <CalendarBlank className="h-4 w-4 shrink-0 self-center text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {dateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span className="text-xs text-muted-foreground">
              · {eventStartLabel(event)} — {eventEndLabel(event)} ({event.span}h)
            </span>
          </div>
          {/* Attribution — shown only when a clerk (or anyone other than you) booked this,
              so you can review who scheduled it. */}
          {detail?.scheduledBy && (
            <div className="flex items-start gap-3 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-3 py-2">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="text-sm">
                <div className="font-medium text-amber-900 dark:text-amber-200">Scheduled by {detail.scheduledBy.name}</div>
                <div className="mt-0.5 text-xs capitalize text-amber-700 dark:text-amber-300">
                  {detail.scheduledBy.role.replace(/_/g, ' ')} · booked this on your diary
                </div>
              </div>
            </div>
          )}
          {/* Location suppressed when the court is already shown in the workspace
              (hearings repeat it); notes suppressed for hearings (auto-generated). */}
          {event.location && !detail?.matter?.court && (
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="text-sm font-medium text-foreground">{event.location}</div>
            </div>
          )}
          {event.notes && !(detail && event.kind === 'hearing') && (
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="text-sm text-foreground">{event.notes}</div>
            </div>
          )}

          {/* Live hearing status from hearings store — hidden when the richer
              lifecycle workspace (below) is loaded to avoid duplication. */}
          {statusCfg && !detail && (
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusCfg.cls}`}>
                {statusCfg.label}
              </span>
              {statusCfg.nextAction && (
                <span className="text-xs text-muted-foreground">→ {statusCfg.nextAction}</span>
              )}
            </div>
          )}

          {/* Outcome recorded */}
          {hearing?.outcome && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                <Trophy className="h-3 w-3" /> Outcome
              </div>
              <p className="text-sm text-foreground">{hearing.outcome}</p>
              {hearing.costsPosition && (
                <p className="mt-1 text-xs text-muted-foreground">Costs: {hearing.costsPosition}</p>
              )}
            </div>
          )}

          {/* Outcome pending alert — replaced by the Action Zone CTA when the workspace loads. */}
          {needsOutcome && !detail && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
              <CheckCircleIcon className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
              <span className="flex-1 text-xs text-amber-800 dark:text-amber-300">Outcome not recorded for this hearing</span>
            </div>
          )}

          {/* RC-DIARY Stage 1c — the workspace, led by a lifecycle-driven Action Zone. */}
          {detail && (
            <DiaryWorkspace
              detail={detail}
              event={event}
              isToday={isToday}
              onClose={onClose}
              onOpenHearing={onOpenHearing}
              onOpenCase={onOpenCase}
            />
          )}

          {(event.caseId || event.hearingId) && (
            <div className="space-y-2 border-t border-border pt-3">
              {isToday && !detail && (
                <Button variant="primary" size="sm" className="w-full justify-between" onClick={() => { onClose(); navigate('/barrister/court-day'); }}>
                  <span>Court Day mode</span>
                  <Gavel className="h-3.5 w-3.5" />
                </Button>
              )}
              {/* Side by side — two stacked full-width links cost a row for nothing. */}
              <div className="grid gap-2 sm:grid-cols-2">
                {event.hearingId && (
                  <Button variant="outline" size="sm" className="w-full justify-between" onClick={onOpenHearing}>
                    <span>Hearing workspace</span>
                    <ArrowSquareOut className="h-3.5 w-3.5" />
                  </Button>
                )}
                {event.caseId && (
                  <Button variant="outline" size="sm" className="w-full justify-between" onClick={onOpenCase}>
                    <span>Matter file</span>
                    <ArrowSquareOut className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>

        {/* Delete / Edit are drawn only when a handler was supplied. A read-only viewer (pupil)
            gets Done alone — the diary write routes refuse them.
            Layout: the things that CHANGE this event sit together on the left; the right-hand
            side holds only the way out. Edit used to sit beside Done, which put an action that
            alters the diary next to the button you press to leave it alone. */}
        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          {/* Icon-only, Edit before Delete: the two controls that change this event, in the
              order you'd reach for them. Each keeps a title + aria-label, so the tooltip and
              every screen reader still say which is which. */}
          <div className="flex items-center gap-2">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                title="Edit"
                aria-label="Edit"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <PencilSimple className="h-4 w-4" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                title="Delete"
                aria-label="Delete"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:border-red-200 hover:bg-red-50 hover:text-danger dark:hover:bg-red-500/10"
              >
                <Trash className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
        </div>
      </Card>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* Diary workspace (RC-DIARY Stage 1c) — Action Zone + progressive   */
/* hierarchy: Action → Readiness → Matter → Money → Supporting.      */
/* ═══════════════════════════════════════════════════════════════ */
function DiaryWorkspace({
  detail,
  event,
  isToday,
  onClose,
  onOpenHearing,
  onOpenCase,
}: {
  detail: DiaryItemDetail;
  event: CalEvent;
  isToday: boolean;
  onClose: () => void;
  onOpenHearing: () => void;
  onOpenCase: () => void;
}) {
  const navigate = useNavigate();
  const m = detail.matter;
  const r = detail.readiness;
  const money = detail.money;
  const fmt = (n: number, ccy: string) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy, maximumFractionDigits: 0 }).format(n);

  // Lifecycle-driven primary action — "what should I do next?", derived client-side
  // from the lifecycle projection (no new backend). Navigates to where the action lives.
  const go = (to: string) => () => { onClose(); navigate(to); };
  const cta: { label: string; onClick: () => void; icon?: JSX.Element } | null = (() => {
    if (event.hearingId) {
      const lc = detail.lifecycle;
      if (lc === 'Awaiting outcome' || lc === 'In court')
        return { label: 'Record outcome', icon: <Gavel className="h-4 w-4" />, onClick: isToday ? go('/barrister/court-day') : onOpenHearing };
      if (lc === 'Billing pending')
        return { label: 'Generate fee note', onClick: go('/barrister/fees') };
      if (lc === 'Preparing' || lc === 'Scheduled' || lc === 'Ready')
        return isToday
          ? { label: 'Open Court Day', icon: <Gavel className="h-4 w-4" />, onClick: go('/barrister/court-day') }
          : { label: 'Prepare hearing', onClick: onOpenHearing };
      return { label: 'Open matter file', onClick: onOpenCase };
    }
    if (event.caseId) return { label: 'Open matter file', onClick: onOpenCase };
    return null;
  })();

  const topMissing = r ? r.missing.slice(0, 3) : [];
  const moneyEmpty = money && money.billed === 0 && money.unbilled === 0 && money.collected === 0 && money.outstanding === 0;

  return (
    <div className="space-y-2.5">
      {/* ── TIER 1 · ACTION ZONE — the first thing the eye lands on ── */}
      <div className="rounded-xl border border-brand-200 bg-brand-50/70 p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {detail.hearing && (
              <div className="text-[10px] font-bold uppercase tracking-wider capitalize text-brand-600 dark:text-brand-400/70">
                {detail.hearing.type.replace(/_/g, ' ')}
              </div>
            )}
            <div className="text-lg font-bold capitalize leading-tight text-brand-900 dark:text-brand-200">{detail.lifecycle}</div>
          </div>
          {(m?.priority === 'high' || m?.priority === 'urgent') && (
            <Badge variant={m.priority === 'urgent' ? 'danger' : 'warning'} className="shrink-0 capitalize">
              {m.priority}
            </Badge>
          )}
        </div>

        {cta && (
          <Button variant="primary" className="mt-3 w-full justify-center" leftIcon={cta.icon} onClick={cta.onClick}>
            {cta.label}
          </Button>
        )}
        {detail.nextAction && detail.nextAction.label !== cta?.label && (
          <p className="mt-1.5 text-center text-[11px] text-brand-700 dark:text-brand-300/80">Suggested: {detail.nextAction.label}</p>
        )}

        {/* Readiness folded into the action zone (Tier 1.5) */}
        {r && (
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-semibold text-brand-900 dark:text-brand-200">
                {r.percentage === 100 ? 'Ready for court' : `${r.percentage}% ready`}
              </span>
              {r.missing.length > 0 && <span className="text-brand-700 dark:text-brand-300/70">{r.missing.length} outstanding</span>}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-foreground/10">
              <div
                className={`h-full rounded-full ${r.percentage >= 75 ? 'bg-emerald-500' : r.percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${r.percentage}%` }}
              />
            </div>
            {topMissing.length > 0 && (
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {topMissing.map((label) => (
                  <span key={label} className="rounded bg-card px-1.5 py-0.5 text-[10px] text-red-700 dark:text-red-300">Missing: {label}</span>
                ))}
                {r.missing.length > topMissing.length && (
                  <span className="text-[10px] text-brand-700 dark:text-brand-300/70">+{r.missing.length - topMissing.length} more</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── TRAVEL — leave-by / arrive-by (canonical travel plan; V1 fixed buffer) ── */}
      {detail.travel && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs">
          <span className="font-semibold text-foreground">
            Leave by {detail.travel.leaveBy.slice(11, 16)}
          </span>
          <span className="text-muted-foreground">· arrive by {detail.travel.arriveBy.slice(11, 16)}</span>
          <span className="text-muted-foreground">· {detail.travel.travelMinutes}m travel</span>
          {(detail.travel.court.postcode || detail.travel.court.city) && (
            <span className="text-muted-foreground">· {[detail.travel.court.city, detail.travel.court.postcode].filter(Boolean).join(' ')}</span>
          )}
        </div>
      )}

      {/* ── TIER 2 · MATTER & PARTIES (matter name/number/type already in the header) ── */}
      {m && (
        <div className="rounded-lg border border-border bg-muted/20 p-2.5 sm:p-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Matter</div>
          <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            <WField label="Status" value={m.status} />
            <WField label="Client" value={m.client} />
            <WField label="Solicitor" value={m.solicitor} />
            <WField label="Opponent" value={m.opponent} />
            <WField label="Court" value={m.court} />
            <WField label="Judge" value={m.judge} />
            <WField label="Room" value={m.courtroom} />
            <WField label="Lead" value={m.leadBarrister} />
            <WField label="Junior" value={m.juniorBarrister} />
            <WField label="Clerk" value={m.clerk} />
          </div>
        </div>
      )}

      {/* ── TIER 3 · MONEY (quiet; soft empty state) ──
          Reads as a statement, not four floating figures: label left, amount right, in one
          bordered panel like every other block here. The left column is the record (billed,
          collected); the right column is what still wants doing (unbilled, outstanding) and is
          the only part allowed to carry colour. A zero is muted — £0 billed should not shout as
          loudly as £20,250 collected. */}
      {money &&
        (moneyEmpty ? (
          <div className="text-xs text-muted-foreground">No fees recorded yet.</div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/20 p-2.5 sm:p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Money</span>
              {money.billingRequired && <Badge variant="warning">Billing required</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:gap-x-6">
              <WMoney label="Billed" value={fmt(money.billed, money.currency)} zero={money.billed === 0} />
              <WMoney label="Unbilled" value={fmt(money.unbilled, money.currency)} amber={money.unbilled > 0} zero={money.unbilled === 0} />
              <WMoney label="Collected" value={fmt(money.collected, money.currency)} zero={money.collected === 0} />
              <WMoney label="Outstanding" value={fmt(money.outstanding, money.currency)} amber={money.outstanding > 0} zero={money.outstanding === 0} />
            </div>
          </div>
        ))}

      {/* ── TIER 4 · SUPPORTING (quietest) ── */}
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <WGlance
          label="Tasks"
          value={`${detail.tasks.open} open${detail.tasks.overdue ? ` · ${detail.tasks.overdue} overdue` : ''}`}
          red={detail.tasks.overdue > 0}
        />
        <WGlance label="Documents" value={`${detail.documents.count}`} />
        <WGlance
          label="Comms"
          value={detail.communications.lastCommunicationAt ? `${detail.communications.threadCount} threads` : 'None'}
        />
      </div>

      {detail.timeline.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Recent activity
          </summary>
          <div className="mt-1 space-y-0.5">
            {detail.timeline.map((t) => (
              <div key={t.id} className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="capitalize">{t.eventType.replace(/_/g, ' ')}</span>
                <span>{new Date(t.occurredAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function WField({ label, value, span }: { label: string; value: string | null; span?: boolean }) {
  if (!value) return null;
  return (
    <div className={`flex items-baseline justify-between gap-3 ${span ? 'sm:col-span-2' : ''}`}>
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="truncate text-right text-xs font-medium capitalize text-foreground">{value}</span>
    </div>
  );
}
function WMoney({ label, value, amber, zero }: { label: string; value: string; amber?: boolean; zero?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-2 sm:gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={`text-sm font-semibold tabular-nums ${
          zero ? 'text-muted-foreground/60' : amber ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
function WGlance({ label, value, red }: { label: string; value: string; red?: boolean }) {
  return (
    <div className="flex items-baseline justify-center gap-1.5 rounded-md border border-border px-2 py-1.5">
      <span className={`text-xs font-semibold ${red ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{value}</span>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* Task detail modal                                               */
/* ═══════════════════════════════════════════════════════════════ */

const TASK_PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
  high:   'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300',
  normal: 'bg-muted text-muted-foreground',
  low:    'bg-muted text-muted-foreground',
};

const TASK_STATUS_COLORS: Record<string, string> = {
  pending:            'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300',
  in_progress:        'bg-brand-100 text-brand-700 dark:text-brand-300',
  awaiting_barrister: 'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
  awaiting_clerk:     'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300',
  hearing_followup:   'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300',
  filing_pending:     'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300',
  blocked:            'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
  completed:          'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  cancelled:          'bg-muted text-muted-foreground',
};

function TaskDetailModal({
  task,
  onClose,
  onOpenCase,
  onOpenHearing,
}: {
  task: Task;
  onClose: () => void;
  onOpenCase: () => void;
  onOpenHearing: () => void;
}) {
  // `slice(0, 10)` first: the API returns a task's dueDate as a full ISO timestamp
  // ("2026-10-09T00:00:00.000Z"), and appending 'T00:00:00' to that produced
  // "…000ZT00:00:00" → Invalid Date, which the modal printed as "Due Invalid Date".
  // Slicing to the date part handles both that and a plain YYYY-MM-DD, and keeps the
  // day LOCAL (parsing the raw ISO would shift the date for anyone east of Greenwich).
  // Same treatment as the diary grid at :212.
  const dueDateObj = task.dueDate ? new Date(task.dueDate.slice(0, 10) + 'T00:00:00') : null;
  const isOverdue = dueDateObj ? dueDateObj < new Date() && task.status !== 'completed' && task.status !== 'cancelled' : false;

  const fmtMins = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
        <Card className="w-full max-w-md overflow-hidden shadow-pop" onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-start justify-between border-b border-border bg-amber-50/60 dark:bg-amber-500/10 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-card">
                <ListChecks className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <Badge>Task</Badge>
                <div className="mt-1 text-base font-semibold text-foreground">{task.title}</div>
                {task.case && (
                  <div className="mt-0.5 text-xs text-muted-foreground">{task.case.caseNumber} — {task.case.caseName}</div>
                )}
              </div>
            </div>
            <button
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <CardContent className="space-y-3 p-6">
            {/* Priority + Status */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${TASK_PRIORITY_COLORS[task.priority] ?? 'bg-muted text-muted-foreground'}`}>
                {PRIORITY_LABELS[task.priority]}
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${TASK_STATUS_COLORS[task.status] ?? 'bg-muted text-muted-foreground'}`}>
                {STATUS_LABELS[task.status]}
              </span>
            </div>

            {/* Due date */}
            {dueDateObj && (
              <div className="flex items-start gap-3">
                <CalendarBlank className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="text-sm">
                  <span className={`font-medium ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                    Due {dueDateObj.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                  {isOverdue && <span className="ml-2 text-xs text-red-500">Overdue</span>}
                </div>
              </div>
            )}

            {/* Description */}
            {task.description && (
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="text-sm text-foreground">{task.description}</div>
              </div>
            )}

            {/* Estimated time */}
            {task.estimatedMinutes != null && (
              <div className="flex items-center gap-3">
                <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  Estimated: <span className="font-medium text-foreground">{fmtMins(task.estimatedMinutes)}</span>
                  {task.actualMinutes != null && (
                    <> · Actual: <span className="font-medium text-foreground">{fmtMins(task.actualMinutes)}</span></>
                  )}
                </div>
              </div>
            )}

            {/* Assigned to / created by */}
            <div className="flex items-start gap-3">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="space-y-0.5 text-xs">
                {task.assignedTo && (
                  <div className="text-muted-foreground">
                    Assigned to: <span className="font-medium text-foreground">{task.assignedTo.firstName} {task.assignedTo.lastName}</span>
                  </div>
                )}
                <div className="text-muted-foreground">
                  Created by: <span className="font-medium text-foreground">{task.createdBy.firstName} {task.createdBy.lastName}</span>
                </div>
              </div>
            </div>

            {/* Navigation */}
            {(task.hearingId || task.caseId) && (
              <div className="space-y-2 border-t border-border pt-3">
                {task.hearingId && (
                  <Button variant="outline" size="sm" className="w-full justify-between" onClick={onOpenHearing}>
                    <span>Open hearing workspace</span>
                    <ArrowSquareOut className="h-3.5 w-3.5" />
                  </Button>
                )}
                {task.caseId && (
                  <Button variant="outline" size="sm" className="w-full justify-between" onClick={onOpenCase}>
                    <span>Open matter file</span>
                    <ArrowSquareOut className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>

          <div className="flex justify-end border-t border-border px-6 py-3">
            <Button variant="primary" size="sm" onClick={onClose}>Close</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/* Calendar sync modal                                             */
/* ═══════════════════════════════════════════════════════════════ */

const COMING_SOON_PROVIDERS = [
  { id: 'apple', name: 'Apple Calendar', icon: AppleLogo, color: 'text-ink-800' },
];

// Live two-way sync providers (Google + Microsoft Outlook). Same modal block, different
// icon/labels and OAuth handshake — they read their slice from the shared status query.
const LIVE_SYNC_PROVIDERS: {
  id: CalendarSyncProviderId; oauth: 'google' | 'microsoft'; name: string; icon: React.ReactNode;
}[] = [
  { id: 'google_calendar', oauth: 'google', name: 'Google Calendar', icon: <GoogleLogo className="h-6 w-6 text-[#4285F4]" weight="fill" /> },
  { id: 'microsoft_365', oauth: 'microsoft', name: 'Microsoft Outlook', icon: <MicrosoftOutlookLogo className="h-6 w-6 text-[#0078D4]" weight="fill" /> },
];

function fmtSync(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function CalendarSyncModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState<CalendarSyncProviderId | null>(null);

  const { data: status, isLoading, isError } = useQuery({
    queryKey: ['calendar-sync-status'],
    queryFn: fetchCalendarSyncStatus,
    refetchInterval: (q) => (q.state.data?.providers?.some((p) => p.syncEnabled) ? 60_000 : false),
  });

  const enableMut = useMutation({ mutationFn: enableCalendarSync, onSuccess: (d) => qc.setQueryData(['calendar-sync-status'], d) });
  const disableMut = useMutation({ mutationFn: disableCalendarSync, onSuccess: (d) => qc.setQueryData(['calendar-sync-status'], d) });
  const resyncMut = useMutation({ mutationFn: resyncCalendar, onSuccess: (d) => qc.setQueryData(['calendar-sync-status'], d) });

  // Lands on calendar settings (where sync is switched on), not the inbox. With a mailboxId it is a
  // reconnect of that same account.
  async function connect(id: CalendarSyncProviderId, oauth: 'google' | 'microsoft', mailboxId?: string) {
    setConnecting(id);
    try {
      const url = await beginOAuthConnect(oauth, { mailboxId, returnTo: 'calendar' });
      window.location.href = url;
    } catch {
      setConnecting(null);
      toast.error(`Could not start ${oauth === 'google' ? 'Google' : 'Microsoft'} connection`);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden bg-black/40 p-2 sm:p-4" onClick={onClose}>
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
        <Card className="w-full min-w-0 max-w-md shadow-pop" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand-50">
                <ArrowsClockwise className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              </div>
              <div>
                <div className="text-base font-semibold text-foreground">Calendar sync</div>
                <div className="text-xs text-muted-foreground">Connect your external calendars</div>
              </div>
            </div>
            <button className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={onClose}>
              <X className="h-4 w-4" />
            </button>
          </div>

          <CardContent className="p-6">
            <div className="space-y-3">
              {/* Google + Microsoft Outlook — LIVE, two-way */}
              {/* A failed status load must not offer "Connect" as though nothing were connected. */}
              {isError && (
                <p role="alert" className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
                  Couldn't load your calendar connections. Close this and try again.
                </p>
              )}
              {!isError && LIVE_SYNC_PROVIDERS.map((p) => {
                const ps = status?.providers.find((x) => x.provider === p.id);
                return (
                  <div key={p.id} className="rounded-lg border border-border p-3 sm:p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
                        {p.icon}
                        <div className="min-w-0">
                          <div className="truncate font-medium text-foreground">{p.name}</div>
                          {(ps?.connected || ps?.needsReconnect) && ps?.emailAddress && (
                            <div className="truncate text-xs text-muted-foreground">{ps.emailAddress}</div>
                          )}
                        </div>
                      </div>

                      {isLoading ? (
                        <div className="h-7 w-24 animate-pulse rounded bg-muted/40" />
                      ) : ps?.needsReconnect ? (
                        <div className="flex shrink-0 items-center gap-2">
                          <Badge variant="default" className="hidden bg-amber-100 text-xs text-amber-700 dark:bg-amber-500/15 dark:text-amber-300 sm:inline-flex">Reconnect needed</Badge>
                          <Button size="xs" variant="primary" disabled={connecting === p.id} onClick={() => connect(p.id, p.oauth, ps.mailboxId ?? undefined)}>
                            {connecting === p.id ? 'Redirecting…' : 'Reconnect'}
                          </Button>
                        </div>
                      ) : !ps?.connected ? (
                        <Button size="xs" variant="primary" disabled={connecting === p.id} onClick={() => connect(p.id, p.oauth)}>
                          {connecting === p.id ? 'Redirecting…' : 'Connect'}
                        </Button>
                      ) : !ps.syncEnabled ? (
                        <Button size="xs" variant="primary" disabled={enableMut.isPending && enableMut.variables === p.id} onClick={() => enableMut.mutate(p.id)}>
                          {enableMut.isPending && enableMut.variables === p.id ? 'Enabling…' : 'Enable sync'}
                        </Button>
                      ) : (
                        <div className="flex shrink-0 items-center gap-2">
                          <Badge variant="default" className="bg-emerald-100 dark:bg-emerald-500/15 text-xs text-emerald-700 dark:text-emerald-300">Syncing</Badge>
                          <Button size="xs" variant="outline" disabled={disableMut.isPending && disableMut.variables === p.id} onClick={() => disableMut.mutate(p.id)}>
                            {disableMut.isPending && disableMut.variables === p.id ? '…' : 'Turn off'}
                          </Button>
                        </div>
                      )}
                    </div>

                    {ps?.connected && !ps.needsReconnect && ps.syncEnabled && (
                      <div className="mt-3 flex items-start justify-between gap-2 border-t border-border/50 pt-3 text-xs text-muted-foreground">
                        <span className="min-w-0 flex-1 break-words">
                          {ps.lastSyncAt ? <>Last synced {fmtSync(ps.lastSyncAt)}</> : 'First sync in progress…'}
                          {ps.lastSyncError && <span className="ml-2 text-amber-700 dark:text-amber-300">⚠ {ps.lastSyncError}</span>}
                        </span>
                        <button
                          type="button"
                          onClick={() => resyncMut.mutate(p.id)}
                          disabled={resyncMut.isPending && resyncMut.variables === p.id}
                          className="flex shrink-0 items-center gap-1 transition hover:text-foreground disabled:opacity-50"
                        >
                          <ArrowsClockwise className="h-3 w-3" />
                          {resyncMut.isPending && resyncMut.variables === p.id ? 'Resyncing…' : 'Resync now'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Apple — coming soon */}
              {COMING_SOON_PROVIDERS.map((provider) => {
                const Icon = provider.icon;
                return (
                  <div key={provider.id} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-6 w-6 ${provider.color}`} weight="fill" />
                      <span className="font-medium text-foreground">{provider.name}</span>
                    </div>
                    <Badge variant="default" className="text-xs">Coming soon</Badge>
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-center text-xs text-muted-foreground">
              Hearings and diary push to your calendar in seconds; changes made there appear within ~5 min — or hit <span className="font-medium text-foreground">Resync now</span>.
            </p>
          </CardContent>

          <div className="flex items-center justify-end border-t border-border px-6 py-4">
            <Button variant="primary" size="md" onClick={onClose}>
              Got it
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
